import { isTenantKind } from './tenantAlteration';

/**
 * How a project status is spelled and coloured on screen.
 *
 * This lived inside ProjectCard until the list view arrived and needed the
 * same two maps. A second copy is how a status added to the enum comes to
 * read "in_progress" in one view and "In Progress" in the other, so there is
 * one copy and both views import it.
 *
 * The keys are the `status` enum on the Project entity in
 * api/src/lib/entities.ts. `attention_required` is not in that enum - it is
 * set on the row by the workflow engine - which is why it is listed here but
 * not in the Projects page filter.
 */
export const PROJECT_STATUS_LABELS = {
  proposed: 'Proposed',
  rfp_sent: 'RFP Sent',
  contracted: 'Contracted',
  in_progress: 'In Progress',
  on_hold_cancelled: 'On Hold',
  on_hold: 'On Hold',
  cancelled: 'Canceled',
  complete: 'Complete',
  approved: 'Approved',
  revises: 'Revises',
  resubmit: 'Resubmit',
  attention_required: '⚠️ Attention Required',
};

export const PROJECT_STATUS_COLORS = {
  proposed: 'bg-muted text-muted-foreground',
  rfp_sent: 'bg-blue-100 text-blue-700',
  contracted: 'bg-violet-100 text-violet-700',
  in_progress: 'bg-emerald-100 text-emerald-700',
  on_hold_cancelled: 'bg-yellow-100 text-yellow-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-red-100 text-red-700',
  complete: 'bg-slate-100 text-slate-700',
  approved: 'bg-emerald-100 text-emerald-700',
  revises: 'bg-amber-100 text-amber-700',
  resubmit: 'bg-orange-100 text-orange-700',
  attention_required: 'bg-red-100 text-red-700',
};

export function projectStatusLabel(status) {
  return PROJECT_STATUS_LABELS[status] || status;
}

export function projectStatusColor(status) {
  return PROJECT_STATUS_COLORS[status] || 'bg-muted text-muted-foreground';
}

/**
 * Whether a project's status is shown at all. A tenant project's is not.
 *
 * These statuses describe capital work - RFP sent, contracted - and the edit
 * form does not ask for one on a tenant project, so a tenant project created
 * there sits at Proposed for good, however far its workflow has got. Showing
 * that as a badge told people a project under construction had not started.
 * Evan's call on 2026-10-19: hide it everywhere it is shown - the dashboard,
 * the project page, the reports and their downloads - until a tenant project
 * has a status that means something. The column is untouched; this only
 * decides what is displayed.
 *
 * "Tenant" is `isTenantKind`: an existing tenant's alteration (migration 024)
 * has no Status field on the form either, so it is stuck at Proposed the same
 * way.
 */
export function showsProjectStatus(project) {
  return !isTenantKind(project?.type);
}

/** `managerOnlyValues.status` on the Project entity in api/src/lib/entities.ts. */
export const RESERVED_STATUSES = ['complete', 'cancelled'];

/** Not active work: finished, paused or abandoned. The dashboard and the
 *  reminder sweeps leave these out. */
export const INACTIVE_STATUSES = ['complete', 'on_hold', 'cancelled', 'on_hold_cancelled'];

/**
 * The statuses the quick picker offers (StatusPicker), in the order a CAPEX or
 * MOE project moves through them. Approved, Revises, Resubmit and Attention
 * Required came over from Base44 and describe plan review, not a project's
 * stage; they stay on the edit form, which still lists every value.
 */
export const QUICK_STATUSES = [
  'proposed', 'rfp_sent', 'contracted', 'in_progress', 'on_hold', 'cancelled', 'complete',
];

/**
 * What the picker lists for one project: the reserved two only for those who
 * may set them (`canCompleteProject`), as on the edit form. A project sitting
 * at a status outside the list keeps it as the first option, so the picker
 * shows what is there rather than a blank.
 */
export function quickStatusOptions(project, canComplete) {
  const options = QUICK_STATUSES.filter(s => canComplete || !RESERVED_STATUSES.includes(s));
  const current = project?.status;
  return current && !options.includes(current) ? [current, ...options] : options;
}
